// frontend/src/components/book/AgentDefaultTemplateModal.jsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from '../shared/Toast';
import ConfirmDialogHost, { useConfirm } from '../shared/ConfirmDialog';

export const GENERAL_PERMS = [
  ['can_update_info', 'Update Info', 'Change name, username, and password'],
  ['can_add_customers', 'Add Customers', 'Create new customer accounts'],
  ['can_add_agents', 'Add Agents', 'Create new agent (sub-agent) accounts'],
  ['can_suspend_wagering', 'Suspend Wagering', "Enable read-only mode on a customer's account"],
  ['can_delete_transactions', 'Delete Transactions', "Void a customer's bets"],
  ['can_deactivate_account', 'Deactivate Account', 'Fully disable a customer\'s login (not the same as read-only — a deactivated account cannot log in at all).'],
  ['can_change_settle_figure', 'Adjust Balance', "Directly edit a customer's balance (e.g. to reset it to zero after an early payoff)"],
  ['can_adjust_free_play_balance', 'Adjust Free Play Balance', 'Directly credit or debit a customer\'s free play balance (not the loss-rebate percentage — that\'s the Free Play Limit toggle below).'],
];

export const SITE_OPTIONS = [
  ['can_allow_deny_casino', 'Allow/Deny Casino', "Change a customer's casino access"],
  ['can_view_ip', 'IP Access', "See a customer's IP address"],
  ['can_access_game_admin', 'Game Admin Access', "Let this agent narrow which of your enabled sports their customers can bet on"],
  ['can_access_feedback', 'Feedback Access', 'Give feedback'],
  ['can_access_settings', 'Cashout Control', "Disable cashout for a specific customer (cannot enable it if you've disabled it book-wide)"],
];

// [key, toggleField, rangeMinField, rangeMaxField, label, defaultFields, placeholders]
// placeholders are the site's standard starting values, shown as greyed-out
// hint text in an unconfigured Default box (still null/unset underneath).
export const LIMIT_TYPES = [
  ['credit', 'can_change_credit_limit', 'credit_limit_min', 'credit_limit_max', 'Change Credit Limit', ['credit_default'], [10000]],
  ['free_play', 'can_change_free_play_limit', 'free_play_limit_min', 'free_play_limit_max', 'Change Free Play Limit (%)', ['free_play_default'], [0]],
  ['wager', 'can_change_wager_limit', 'wager_limit_min', 'wager_limit_max', 'Change Wager Limit', ['wager_default_min', 'wager_default_max'], [0.01, 1000]],
  ['live_wager', 'can_change_live_wager_limit', 'live_wager_limit_min', 'live_wager_limit_max', 'Change Live Wager Limit', ['live_wager_default_min', 'live_wager_default_max'], [10, 1000]],
  ['parlay', 'can_change_parlay_limit', 'parlay_limit_min', 'parlay_limit_max', 'Change Parlay Limit', ['parlay_default_min', 'parlay_default_max'], [0.01, 500]],
  ['contest', 'can_change_contest_limit', 'contest_limit_min', 'contest_limit_max', 'Change Contest Limit', ['contest_default'], [0]],
  ['casino', 'can_change_casino_limit', 'casino_limit_min', 'casino_limit_max', 'Change Casino Limit', ['casino_default_min', 'casino_default_max'], [10, 1000]],
  ['casino_weekly', 'can_change_casino_weekly_limit', 'casino_weekly_limit_min', 'casino_weekly_limit_max', 'Change Casino Weekly Win/Loss Limit', ['casino_weekly_win_default', 'casino_weekly_loss_default'], [0, 0]],
];

export default function AgentDefaultTemplateModal({ agentCount, onClose, onApplied }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [openSections, setOpenSections] = useState(new Set(['general']));
  const { toast, showToast } = useToast();
  const { confirmState, confirm, resolveConfirm } = useConfirm();

  useEffect(() => {
    api.getAgentDefaultTemplate()
      .then(data => setTemplate(data))
      .catch(err => setLoadError(err.response?.data?.error || 'Failed to load default agent template'))
      .finally(() => setLoading(false));
  }, []);

  const toggleSection = (key) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const setBool = (field, value) => setTemplate(prev => ({ ...prev, [field]: value }));
  const setNum = (field, value) => setTemplate(prev => ({ ...prev, [field]: Number(value) }));
  const setDefault = (field, value) => setTemplate(prev => ({ ...prev, [field]: value === '' ? null : Number(value) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateAgentDefaultTemplate(template);
      setTemplate(updated);
      showToast('Default agent template saved!');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    const ok = await confirm(`This will overwrite EVERY agent (${agentCount}) in your hierarchy with this template's settings — base limits, agent cut, and all permissions. This cannot be undone. Continue?`);
    if (!ok) return;
    setApplying(true);
    try {
      await api.applyAgentDefaultTemplate();
      onApplied();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to apply template', 'error');
    } finally {
      setApplying(false);
    }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' };
  const modalStyle = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, maxWidth: '56rem', width: '100%', maxHeight: '90vh', overflowY: 'auto', fontFamily };
  const sectionCard = { background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, marginBottom: '1rem' };
  const sectionHeader = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.textPrimary, fontFamily, fontWeight: 600, fontSize: '1rem' };
  const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };
  const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
  const numInputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.3rem 0.5rem', width: 90, boxSizing: 'border-box', fontSize: '0.8rem', fontFamily };
  const checkboxRow = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.5rem 0', borderBottom: `1px solid ${colors.sectionBg}` };
  const saveButtonStyle = { background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };
  const applyButtonStyle = { background: 'rgba(74,222,128,0.15)', border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };

  const renderCheckbox = (field, label, hint) => (
    <label key={field} style={{ ...checkboxRow, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!template[field]} onChange={e => setBool(field, e.target.checked)} style={{ marginTop: 3, accentColor: colors.gold, cursor: 'pointer' }} />
      <div>
        <div style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</div>
        <div style={{ color: colors.textMuted, fontSize: '0.78rem' }}>{hint}</div>
      </div>
    </label>
  );

  return (
    <div style={overlayStyle}>
      <ToastHost toast={toast} />
      <ConfirmDialogHost confirmState={confirmState} resolveConfirm={resolveConfirm} confirmLabel="Apply to All" />
      <div className="scrollbar-hide" style={modalStyle}>
        <div style={{ padding: '1.25rem', borderBottom: `1px solid ${colors.goldMuted}`, position: 'sticky', top: 0, background: colors.bgGradient, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: colors.textPrimary, margin: 0 }}>Default Agent Template</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textPrimary, fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
          </div>
          <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.3rem' }}>
            Every setting you can change about an agent, saved once and pushed onto all {agentCount} agent(s) in your hierarchy.
          </p>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {loading ? (
            <p style={{ color: colors.textMuted, fontFamily }}>Loading template...</p>
          ) : loadError || !template ? (
            <p style={{ color: colors.negative, fontFamily }}>{loadError || 'Failed to load template'}</p>
          ) : (
            <>
              <div style={sectionCard}>
                <button onClick={() => toggleSection('general')} style={sectionHeader}>
                  <span>General Account Setup</span>
                  {openSections.has('general') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                </button>
                {openSections.has('general') && (
                  <div style={{ padding: '0 1.25rem 1.25rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={labelStyle}>Weekly Limit ($)</label>
                        <input type="number" style={inputStyle} value={template.weekly_limit} onChange={e => setNum('weekly_limit', e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Agent Cut (%)</label>
                        <input type="number" min="0" max="100" step="0.1" style={inputStyle} value={template.agent_cut} onChange={e => setNum('agent_cut', e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Min Bet ($)</label>
                        <input type="number" step="0.01" min="0" style={inputStyle} value={template.min_bet} onChange={e => setNum('min_bet', e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Max Bet ($)</label>
                        <input type="number" step="0.01" min="0" style={inputStyle} value={template.max_bet} onChange={e => setNum('max_bet', e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Parlay Min Bet ($)</label>
                        <input type="number" step="0.01" min="0" style={inputStyle} value={template.parlay_min_bet} onChange={e => setNum('parlay_min_bet', e.target.value)} />
                      </div>
                      <div>
                        <label style={labelStyle}>Parlay Max Bet ($)</label>
                        <input type="number" step="0.01" min="0" style={inputStyle} value={template.parlay_max_bet} onChange={e => setNum('parlay_max_bet', e.target.value)} />
                      </div>
                    </div>
                    {GENERAL_PERMS.map(([field, label, hint]) => renderCheckbox(field, label, hint))}
                  </div>
                )}
              </div>

              <div style={sectionCard}>
                <button onClick={() => toggleSection('limits')} style={sectionHeader}>
                  <span>Limits</span>
                  {openSections.has('limits') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                </button>
                {openSections.has('limits') && (
                  <div style={{ padding: '0 1.25rem 1.25rem' }}>
                    <p style={{ color: colors.textFaint, fontSize: '0.75rem', marginTop: 0, marginBottom: '0.75rem' }}>
                      Range = how far an agent may set this for their own customers, when the box is checked.<br />
                      Default = what a customer gets instead, when it's unchecked.
                    </p>
                    {LIMIT_TYPES.map(([key, toggleField, minField, maxField, label, defaultFields, placeholders]) => (
                      <div key={key} style={{ padding: '0.6rem 0', borderBottom: `1px solid ${colors.sectionBg}` }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                          <input type="checkbox" checked={!!template[toggleField]} onChange={e => setBool(toggleField, e.target.checked)} style={{ accentColor: colors.gold, cursor: 'pointer' }} />
                          <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
                        </label>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem', marginBottom: '0.35rem' }}>
                          <span style={{ color: colors.textPrimary, fontSize: '0.85rem', fontWeight: 700, width: 58 }}>Range</span>
                          <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                            Min <input type="number" step="0.01" min="0" value={template[minField]} onChange={e => setNum(minField, e.target.value)} style={numInputStyle} />
                          </label>
                          <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                            Max <input type="number" step="0.01" min="0" value={template[maxField]} onChange={e => setNum(maxField, e.target.value)} style={numInputStyle} />
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem' }}>
                          <span style={{ color: colors.textPrimary, fontSize: '0.85rem', fontWeight: 700, width: 58 }}>Default</span>
                          {defaultFields.length === 2 ? (
                            <>
                              <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                Min <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[0]} value={template[defaultFields[0]] ?? ''} onChange={e => setDefault(defaultFields[0], e.target.value)} style={numInputStyle} />
                              </label>
                              <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                Max <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[1]} value={template[defaultFields[1]] ?? ''} onChange={e => setDefault(defaultFields[1], e.target.value)} style={numInputStyle} />
                              </label>
                            </>
                          ) : (
                            <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[0]} value={template[defaultFields[0]] ?? ''} onChange={e => setDefault(defaultFields[0], e.target.value)} style={numInputStyle} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={sectionCard}>
                <button onClick={() => toggleSection('siteOptions')} style={sectionHeader}>
                  <span>Site Options</span>
                  {openSections.has('siteOptions') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                </button>
                {openSections.has('siteOptions') && (
                  <div style={{ padding: '0 1.25rem 1.25rem' }}>
                    {SITE_OPTIONS.map(([field, label, hint]) => renderCheckbox(field, label, hint))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button onClick={handleSave} disabled={saving} style={saveButtonStyle}>
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button onClick={handleApply} disabled={applying || agentCount === 0} style={applyButtonStyle}>
                  {applying ? 'Applying...' : `Apply to All Agents (${agentCount})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
